import type { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { useCallback, useRef, useState } from 'react';
import { errorMessage } from '../i18n/errorMessages';
import { ApiError, setAgentDisabled, type Agent } from '../lib/api';

export type LifecyclePending = 'disable' | 'enable' | null;

export interface AgentLifecycle {
  disabled: boolean;
  pending: LifecyclePending;
  /** Failure of the disable click, rendered inside the lifecycle section. */
  disableError: MessageDescriptor | null;
  /** Failure of an enable, rendered under the page banner. */
  enableError: MessageDescriptor | null;
  disable: () => Promise<void>;
  enable: () => Promise<void>;
  dismissEnableError: () => void;
}

const errorText = (err: unknown): MessageDescriptor =>
  errorMessage(err, msg`Could not update this Agent. Please try again.`);

const enableFailure = (error: unknown, agentId: string): MessageDescriptor => {
  // On the owner activation endpoint, an uncoded conflict specifically means
  // an unrestricted grant prevented activation. Permission replacement is lossy.
  if (error instanceof ApiError && error.status === 409 && !error.code) {
    const permissionsPath = `/api/v1/agents/${encodeURIComponent(agentId)}/permissions`;
    return msg`This Agent has an unrestricted '*' permission grant and cannot be enabled. Clearing its permissions deletes all grants, so first read and save them with GET ${permissionsPath}. Clear the entire set with PUT ${permissionsPath} and an empty list. Then enable the Agent and restore the saved permissions with PUT ${permissionsPath}, excluding every '*' grant at every scope.`;
  }
  return errorText(error);
};

interface Options {
  agentId: string;
  agent: Agent;
  /** Publishes authoritative server state back to whoever renders the agent. */
  onUpdated: (agent: Agent) => void;
  /** Toast copy; the banner carries the durable undo, this is the cheap one. */
  onToast: (
    message: MessageDescriptor,
    action?: { label: MessageDescriptor; onClick: () => void },
  ) => void;
}

export function useAgentLifecycle({ agentId, agent, onUpdated, onToast }: Options): AgentLifecycle {
  const [pending, setPending] = useState<LifecyclePending>(null);
  const [disableError, setDisableError] = useState<MessageDescriptor | null>(null);
  const [enableError, setEnableError] = useState<MessageDescriptor | null>(null);
  // The toast's undo action closes over the render that existed before disable()
  // started, where `pending` was still null. The guard therefore has to be a
  // shared cell rather than that captured value, or the undo fires a second
  // write on top of the disable it is undoing.
  const inFlight = useRef(false);

  const disabled = agent.disabled;

  const enable = useCallback(async () => {
    if (inFlight.current) return;
    try {
      inFlight.current = true;
      setPending('enable');
      setEnableError(null);
      onUpdated(await setAgentDisabled(agentId, false));
      onToast(msg`${agent.display_name} enabled`);
    } catch (err) {
      setEnableError(enableFailure(err, agentId));
    } finally {
      inFlight.current = false;
      setPending(null);
    }
  }, [agent.display_name, agentId, onToast, onUpdated]);

  const disable = useCallback(async () => {
    if (inFlight.current) return;
    try {
      inFlight.current = true;
      setPending('disable');
      setDisableError(null);
      onUpdated(await setAgentDisabled(agentId, true));
      onToast(msg`${agent.display_name} disabled`, {
        label: msg`Enable`,
        onClick: () => {
          void enable();
        },
      });
    } catch (err) {
      setDisableError(errorText(err));
    } finally {
      inFlight.current = false;
      setPending(null);
    }
  }, [agent.display_name, agentId, enable, onToast, onUpdated]);

  const dismissEnableError = useCallback(() => setEnableError(null), []);

  return {
    disabled,
    pending,
    disableError,
    enableError,
    disable,
    enable,
    dismissEnableError,
  };
}
